import React, { useCallback, useEffect, useState } from 'react'
import {
  StyledCol,
  StyledColContainer,
  StyledContainer,
  StyledContinueButtonContainer,
  StyledHeader,
  StyledRowContainer,
} from './ValueBasicInformationForm.styled'
import Input from 'components/Input/Input'
import { InputAdornment } from '@material-ui/core'
import { useAppDispatch } from 'store/hooks'
import { useSelector } from 'react-redux'
import { removeFormField, selectAddressFormData, setFormField } from 'slices/addressFormSlice'
import { useTranslation } from 'react-i18next'
import {
  BasementConversionType,
  BasementType,
  BuildingType,
  ConstructionType,
  EnergyClassType,
  ObjectType,
  OutdoorFacilitiesType,
  RoofType,
} from 'types/ApiResponse'
import Button from 'components/Button/Button'
import MultiSelect from 'components/MultiSelect/MultiSelect'
import {
  basementConversionTypes,
  basementTypes,
  constructionTypes,
  elevatorSelect,
  energyClassTypes,
  gardenSelect,
  handleNumberInput,
  outdoorFacilitiesTypes,
  roofTypes,
} from 'components/Modal/ProductFormModal/helper'
import RadioGroupButtons from 'components/RadioGroupButtons/RadioGroupButtons'

const ValueBasicInformationForm = (props: any) => {
  const { setCurrentSubTab } = props
  const dispatch = useAppDispatch()
  const addressFormData = useSelector(selectAddressFormData)
  const { t } = useTranslation()
  const [showValidation, setShowValidation] = useState(false)
  const [validationErrors, setValidationErrors] = useState([])
  const objectType = addressFormData.addressData?.propertyDetails?.objectType
  const buildingType = addressFormData.addressData?.propertyDetails?.buildingType
  const numberRegex = /^\d*(,\d{0,2})?$/
  const numberOfUnitsLabel =
    objectType === ObjectType.APARTMENT
      ? t('newAddressModal.objectDataTab.numberOfUnits')
      : t('newAddressModal.objectDataTab.numberOfResidentialUnits')

  const validateNumericField = (currentValue, min, max, key, isFloat = false) => {
    const numericValue = isFloat
      ? parseFloat(currentValue.replace(',', '.'))
      : parseInt(currentValue)
    const numericMin = parseInt(min)
    const numericMax = parseInt(max)
    if (numericValue >= numericMax) {
      dispatch(setFormField({ key, value: `${numericMax}` }))
    } else if (numericValue <= numericMin) {
      dispatch(setFormField({ key, value: `${numericMin}` }))
    }
  }

  const validate = (name, property) => {
    if (property === null || property === undefined || property === '') {
      if (!validationErrors.includes(name)) {
        setValidationErrors((errors) => [name, ...errors])
      }
      return showValidation && true
    } else {
      if (validationErrors.includes(name)) {
        setValidationErrors((errors) => errors.filter((error) => error !== name))
      }
      return false
    }
  }

  const validateArea = (name, property) => {
    if (buildingType !== BuildingType.RESIDENTIAL_AND_COMMERCIAL) {
      return validate(name, property)
    }
    if (
      addressFormData.addressData?.propertyDetails?.commercialArea &&
      addressFormData.addressData?.propertyDetails?.livingArea
    ) {
      const commercialArea = parseFloat(
        addressFormData.addressData?.propertyDetails?.commercialArea
      )
      const livingArea = parseFloat(addressFormData.addressData?.propertyDetails?.livingArea)
      if (commercialArea >= (commercialArea + livingArea) * 0.5) {
        if (!validationErrors.includes(name)) {
          setValidationErrors((errors) => [name, ...errors])
        }
        return showValidation && true
      }
    }
    return validate(name, property)
  }

  const validationMultiSelect = (value, options, formFieldName) => {
    if (!options) {
      return value
    }
    const matchingOption = options.find(
      (option) => option.value === `${value}` || option.key === `${value}`
    )
    if (
      (value !== null && value !== undefined && !matchingOption) ||
      value === null ||
      value === undefined
    ) {
      dispatch(removeFormField(formFieldName))
      return false
    }
    return true
  }

  const onChangeBuildYear = (constructionYear: string, validate: boolean = false) => {
    if (constructionYear && constructionYear.length === 0) {
      dispatch(removeFormField('propertyDetails.constructionYear'))
      return
    }
    dispatch(setFormField({ key: 'propertyDetails.constructionYear', value: constructionYear }))
    if (validate) {
      validateNumericField(
        constructionYear,
        1800,
        new Date().getFullYear(),
        'propertyDetails.constructionYear'
      )
    }
  }

  const onChangeLivingArea = (livingArea: string, validate: boolean = false) => {
    if (livingArea && livingArea.length === 0) {
      dispatch(removeFormField('propertyDetails.livingArea'))
      return
    }
    if (numberRegex.test(livingArea)) {
      dispatch(setFormField({ key: 'propertyDetails.livingArea', value: livingArea }))
      if (validate) {
        validateNumericField(
          livingArea,
          1,
          objectType === ObjectType.MULTI_FAMILY_HOUSE ? 1000 : 500,
          'propertyDetails.livingArea',
          true
        )
      }
    }
  }

  const onChangePlotArea = (plotArea: string, validate: boolean = false) => {
    if (plotArea && plotArea.length === 0) {
      dispatch(removeFormField('propertyDetails.plotArea'))
      return
    }
    if (numberRegex.test(plotArea)) {
      dispatch(setFormField({ key: 'propertyDetails.plotArea', value: plotArea }))
      if (validate) {
        validateNumericField(plotArea, 1, 5000, 'propertyDetails.plotArea', true)
      }
    }
  }

  const onChangeCommercialArea = (commercialArea: string, validate: boolean = false) => {
    if (commercialArea && commercialArea.length === 0) {
      dispatch(removeFormField('propertyDetails.commercialArea'))
      return
    }
    if (numberRegex.test(commercialArea)) {
      dispatch(setFormField({ key: 'propertyDetails.commercialArea', value: commercialArea }))
      if (validate) {
        validateNumericField(commercialArea, 1, 1000000, 'propertyDetails.commercialArea', true)
      }
    }
  }

  const onChangeNumberOfFloors = (numberOfFloors: string, validate: boolean = false) => {
    if (numberOfFloors && numberOfFloors.length === 0) {
      dispatch(removeFormField('propertyDetails.numberOfFloors'))
      return
    }
    dispatch(setFormField({ key: 'propertyDetails.numberOfFloors', value: numberOfFloors }))
    if (validate) {
      validateNumericField(
        numberOfFloors,
        1,
        objectType === ObjectType.HOUSE ? 3 : 1000,
        'propertyDetails.numberOfFloors'
      )
    }
  }

  const onChangeConstruction = useCallback(
    (construction: ConstructionType) => {
      dispatch(setFormField({ key: 'propertyDetails.construction', value: construction }))
    },
    [dispatch]
  )

  const onChangeRoof = useCallback(
    (roof: RoofType) => {
      dispatch(setFormField({ key: 'propertyDetails.roof', value: roof }))
    },
    [dispatch]
  )

  const onChangeBasement = useCallback(
    (basement: BasementType) => {
      dispatch(setFormField({ key: 'propertyDetails.basement', value: basement }))
      if (basement === BasementType.NONE) {
        dispatch(removeFormField('propertyDetails.basementConversion'))
        if (validationErrors.includes('basementConversion')) {
          setValidationErrors((errors) => errors.filter((error) => error !== 'basementConversion'))
        }
      }
    },
    [dispatch, setValidationErrors, validationErrors]
  )

  const onChangeBasementConversion = useCallback(
    (basementConversion: BasementConversionType) => {
      dispatch(
        setFormField({ key: 'propertyDetails.basementConversion', value: basementConversion })
      )
    },
    [dispatch]
  )

  const onChangeOutdoorFacilities = useCallback(
    (outdoorFacilities: OutdoorFacilitiesType) => {
      dispatch(setFormField({ key: 'propertyDetails.outdoorFacilities', value: outdoorFacilities }))
    },
    [dispatch]
  )

  const onChangeGarages = (garages: boolean, validate: boolean = false) => {
    dispatch(setFormField({ key: 'propertyDetails.garages', value: garages }))
    if (validate) {
      if (objectType === ObjectType.HOUSE) {
        validateNumericField(garages, 0, 4, 'propertyDetails.garages')
      } else {
        validateNumericField(garages, 0, 1000000, 'propertyDetails.garages')
      }
    }
  }

  const onChangeOutdoorParkingSpaces = (
    outdoorParkingSpaces: string,
    validate: boolean = false
  ) => {
    if (outdoorParkingSpaces && outdoorParkingSpaces.length === 0) {
      dispatch(removeFormField('propertyDetails.outdoorParkingSpaces'))
      return
    }
    dispatch(
      setFormField({ key: 'propertyDetails.outdoorParkingSpaces', value: outdoorParkingSpaces })
    )
    if (validate) {
      validateNumericField(outdoorParkingSpaces, 0, 1000000, 'propertyDetails.outdoorParkingSpaces')
    }
  }

  const onChangeFloors = (floor: string, validate: boolean = false) => {
    if (floor && floor.length === 0) {
      dispatch(removeFormField('propertyDetails.floorNumber'))
      return
    }
    dispatch(setFormField({ key: 'propertyDetails.floorNumber', value: floor }))
    if (validate) {
      validateNumericField(floor, 0, 100, 'propertyDetails.floorNumber')
    }
  }

  const onChangeNumberOfUnits = (numberOfUnits: string, validate: boolean = false) => {
    if (numberOfUnits && numberOfUnits.length === 0) {
      dispatch(removeFormField('propertyDetails.numberOfUnits'))
      return
    }
    dispatch(setFormField({ key: 'propertyDetails.numberOfUnits', value: numberOfUnits }))
    if (validate) {
      validateNumericField(
        numberOfUnits,
        buildingType === BuildingType.MULTI_FAMILY_HOUSE ? 3 : 1,
        1000000,
        'propertyDetails.numberOfUnits'
      )
    }
  }

  const onChangeNumberOfCommercialUnits = (
    numberOfCommercialUnits: string,
    validate: boolean = false
  ) => {
    if (numberOfCommercialUnits && numberOfCommercialUnits.length === 0) {
      dispatch(removeFormField('propertyDetails.numberOfCommercialUnits'))
      return
    }
    dispatch(
      setFormField({
        key: 'propertyDetails.numberOfCommercialUnits',
        value: numberOfCommercialUnits,
      })
    )
    if (validate) {
      validateNumericField(
        numberOfCommercialUnits,
        1,
        1000000,
        'propertyDetails.numberOfCommercialUnits'
      )
    }
  }

  const onChangeElevator = (elevator: boolean) => {
    dispatch(setFormField({ key: 'propertyDetails.elevator', value: elevator }))
  }

  const onChangeGarden = (garden: boolean) => {
    dispatch(setFormField({ key: 'propertyDetails.garden', value: garden }))
  }

  const onChangeEnergyClass = (energyClass: EnergyClassType) => {
    dispatch(
      setFormField({
        key: 'propertyDetails.energyCertificate.energyClass',
        value: energyClass,
      })
    )
  }

  const onBackClick = () => {
    setCurrentSubTab((tab) => tab - 1)
  }

  const onContinueClick = () => {
    setShowValidation(true)
    if (validationErrors.length === 0) {
      setCurrentSubTab((tab) => tab + 1)
    }
  }

  useEffect(() => {
    if (
      objectType === ObjectType.HOUSE &&
      !addressFormData.addressData?.propertyDetails?.construction
    ) {
      onChangeConstruction(ConstructionType.MASSIVE)
    }
    if (objectType === ObjectType.HOUSE && !addressFormData.addressData?.propertyDetails?.roof) {
      onChangeRoof(RoofType.FULLY_DEVELOPED)
    }
    if (
      objectType === ObjectType.HOUSE &&
      !addressFormData.addressData?.propertyDetails?.basement
    ) {
      onChangeBasement(BasementType.FULL)
    }
    if (
      objectType === ObjectType.HOUSE &&
      !addressFormData.addressData?.propertyDetails?.basement &&
      !addressFormData.addressData?.propertyDetails?.basementConversion
    ) {
      onChangeBasementConversion(BasementConversionType.NONE)
    }
    if (
      objectType === ObjectType.HOUSE &&
      !addressFormData.addressData?.propertyDetails?.outdoorFacilities
    ) {
      onChangeOutdoorFacilities(OutdoorFacilitiesType.AVERAGE)
    }
  }, [
    objectType,
    addressFormData.addressData,
    onChangeConstruction,
    onChangeRoof,
    onChangeBasement,
    onChangeBasementConversion,
    onChangeOutdoorFacilities,
  ])

  useEffect(() => {
    onChangeBuildYear(addressFormData.addressData?.propertyDetails?.constructionYear, true)
    onChangeLivingArea(addressFormData.addressData?.propertyDetails?.livingArea, true)
    onChangeCommercialArea(addressFormData.addressData?.propertyDetails?.commercialArea, true)
    onChangePlotArea(addressFormData.addressData?.propertyDetails?.plotArea, true)
    onChangeNumberOfFloors(addressFormData.addressData?.propertyDetails?.numberOfFloors, true)
    if (addressFormData.addressData?.propertyDetails?.basement) {
      onChangeBasement(addressFormData.addressData?.propertyDetails?.basement)
    }
    onChangeGarages(addressFormData.addressData?.propertyDetails?.garages)
    onChangeOutdoorParkingSpaces(
      addressFormData.addressData?.propertyDetails?.outdoorParkingSpaces,
      true
    )
    onChangeFloors(addressFormData.addressData?.propertyDetails?.floorNumber, true)
    onChangeNumberOfUnits(addressFormData.addressData?.propertyDetails?.numberOfUnits, true)
    onChangeNumberOfCommercialUnits(
      addressFormData.addressData?.propertyDetails?.numberOfCommercialUnits,
      true
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <>
      <StyledContainer>
        <StyledHeader>{t('newAddressModal.objectDataTab.headers.basic_information')}</StyledHeader>
        <StyledRowContainer>
          <StyledColContainer>
            <StyledCol
              validationError={validate(
                'constructionYear',
                addressFormData.addressData?.propertyDetails?.constructionYear
              )}
            >
              <Input
                value={addressFormData.addressData?.propertyDetails?.constructionYear}
                onChange={(event) => onChangeBuildYear(event.target.value)}
                onBlur={(event) => onChangeBuildYear(event.target.value, true)}
                onKeyPress={(event) => handleNumberInput(event)}
                label={`${t('newAddressModal.objectDataTab.buildYear')}*`}
                helperText={`1800 – ${t('common.today')}`}
                defaultValue={addressFormData.addressData?.propertyDetails?.constructionYear}
              />
            </StyledCol>
          </StyledColContainer>
          <StyledColContainer>
            <StyledCol
              validationError={validateArea(
                'livingArea',
                addressFormData.addressData?.propertyDetails?.livingArea
              )}
            >
              <Input
                value={addressFormData.addressData?.propertyDetails?.livingArea}
                onChange={(event) => onChangeLivingArea(event.target.value)}
                onBlur={(event) => onChangeLivingArea(event.target.value, true)}
                defaultValue={addressFormData.addressData?.propertyDetails?.livingArea}
                onKeyPress={(event) => handleNumberInput(event, true)}
                label={`${t('newAddressModal.objectDataTab.livingArea')}*`}
                helperText={`max. ${objectType === ObjectType.MULTI_FAMILY_HOUSE ? '1000' : '500'}`}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end" style={{ color: '#92969a' }}>
                      m2
                    </InputAdornment>
                  ),
                }}
              />
            </StyledCol>
          </StyledColContainer>
          {buildingType === BuildingType.RESIDENTIAL_AND_COMMERCIAL && (
            <StyledColContainer>
              <StyledCol
                validationError={validateArea(
                  'commercialArea',
                  addressFormData.addressData?.propertyDetails?.commercialArea
                )}
              >
                <Input
                  value={addressFormData.addressData?.propertyDetails?.commercialArea}
                  onChange={(event) => onChangeCommercialArea(event.target.value)}
                  onBlur={(event) => onChangeCommercialArea(event.target.value, true)}
                  onKeyPress={(event) => handleNumberInput(event, true)}
                  defaultValue={addressFormData.addressData?.propertyDetails?.commercialArea}
                  label={`${t('newAddressModal.objectDataTab.commercialArea')}*`}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end" style={{ color: '#92969a' }}>
                        m2
                      </InputAdornment>
                    ),
                  }}
                />
              </StyledCol>
            </StyledColContainer>
          )}
          {(objectType === ObjectType.HOUSE || objectType === ObjectType.MULTI_FAMILY_HOUSE) && (
            <StyledColContainer>
              <StyledCol
                validationError={validate(
                  'plotArea',
                  addressFormData.addressData?.propertyDetails?.plotArea
                )}
              >
                <Input
                  value={addressFormData.addressData?.propertyDetails?.plotArea}
                  onChange={(event) => onChangePlotArea(event.target.value)}
                  onBlur={(event) => onChangePlotArea(event.target.value, true)}
                  onKeyPress={(event) => handleNumberInput(event, true)}
                  defaultValue={addressFormData.addressData?.propertyDetails?.plotArea}
                  label={`${t('newAddressModal.objectDataTab.plotArea')}*`}
                  helperText={'max. 5000'}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end" style={{ color: '#92969a' }}>
                        m2
                      </InputAdornment>
                    ),
                  }}
                />
              </StyledCol>
            </StyledColContainer>
          )}
          {objectType !== ObjectType.APARTMENT && (
            <StyledColContainer>
              <StyledCol
                validationError={validate(
                  'numberOfFloors',
                  addressFormData.addressData?.propertyDetails?.numberOfFloors
                )}
              >
                <Input
                  value={addressFormData.addressData?.propertyDetails?.numberOfFloors}
                  onChange={(event) => onChangeNumberOfFloors(event.target.value)}
                  onBlur={(event) => onChangeNumberOfFloors(event.target.value, true)}
                  onKeyPress={(event) => handleNumberInput(event)}
                  defaultValue={addressFormData.addressData?.propertyDetails?.numberOfFloors}
                  label={`${t('newAddressModal.objectDataTab.numberOfFloors')}*`}
                  helperText={`max. ${objectType === ObjectType.HOUSE ? '3' : '1000'}`}
                />
              </StyledCol>
            </StyledColContainer>
          )}
          {objectType !== ObjectType.HOUSE && (
            <StyledColContainer>
              <StyledCol
                validationError={validate(
                  'numberOfUnits',
                  addressFormData.addressData?.propertyDetails?.numberOfUnits
                )}
              >
                <Input
                  value={addressFormData.addressData?.propertyDetails?.numberOfUnits}
                  onChange={(event) => onChangeNumberOfUnits(event.target.value)}
                  onBlur={(event) => onChangeNumberOfUnits(event.target.value, true)}
                  onKeyPress={(event) => handleNumberInput(event)}
                  defaultValue={addressFormData.addressData?.propertyDetails?.numberOfUnits}
                  label={`${numberOfUnitsLabel}*`}
                  helperText={buildingType === BuildingType.MULTI_FAMILY_HOUSE ? 'min. 3' : null}
                />
              </StyledCol>
            </StyledColContainer>
          )}
          {buildingType === BuildingType.RESIDENTIAL_AND_COMMERCIAL && (
            <StyledColContainer>
              <StyledCol
                validationError={validate(
                  'numberOfCommercialUnits',
                  addressFormData.addressData?.propertyDetails?.numberOfCommercialUnits
                )}
              >
                <Input
                  value={addressFormData.addressData?.propertyDetails?.numberOfCommercialUnits}
                  onChange={(event) => onChangeNumberOfCommercialUnits(event.target.value)}
                  onBlur={(event) => onChangeNumberOfCommercialUnits(event.target.value, true)}
                  onKeyPress={(event) => handleNumberInput(event)}
                  defaultValue={
                    addressFormData.addressData?.propertyDetails?.numberOfCommercialUnits
                  }
                  label={`${t('newAddressModal.objectDataTab.numberOfCommercialUnits')}*`}
                />
              </StyledCol>
            </StyledColContainer>
          )}
          {objectType === ObjectType.HOUSE && (
            <StyledColContainer>
              <StyledCol
                validationError={validate(
                  'construction',
                  addressFormData.addressData?.propertyDetails?.construction
                )}
              >
                <MultiSelect
                  value={addressFormData.addressData?.propertyDetails?.construction || ''}
                  options={constructionTypes}
                  label={`${t('newAddressModal.objectDataTab.construction')}*`}
                  onChange={(event) => onChangeConstruction(event.target.value)}
                  validation={validationMultiSelect}
                  formFieldName="propertyDetails.construction"
                />
              </StyledCol>
            </StyledColContainer>
          )}
          {objectType === ObjectType.HOUSE && (
            <StyledColContainer>
              <StyledCol
                validationError={validate(
                  'roof',
                  addressFormData.addressData?.propertyDetails?.roof
                )}
              >
                <MultiSelect
                  value={addressFormData.addressData?.propertyDetails?.roof || ''}
                  options={roofTypes}
                  label={`${t('newAddressModal.objectDataTab.roof')}*`}
                  onChange={(event) => onChangeRoof(event.target.value)}
                  validation={validationMultiSelect}
                  formFieldName="propertyDetails.roof"
                />
              </StyledCol>
            </StyledColContainer>
          )}
          {objectType === ObjectType.HOUSE && (
            <StyledColContainer>
              <StyledCol
                validationError={validate(
                  'basement',
                  addressFormData.addressData?.propertyDetails?.basement
                )}
              >
                <MultiSelect
                  value={addressFormData.addressData?.propertyDetails?.basement || ''}
                  options={basementTypes}
                  label={`${t('newAddressModal.objectDataTab.basement')}*`}
                  onChange={(event) => onChangeBasement(event.target.value)}
                  validation={validationMultiSelect}
                  formFieldName="propertyDetails.basement"
                />
              </StyledCol>
            </StyledColContainer>
          )}
          {objectType === ObjectType.HOUSE && (
            <StyledColContainer>
              <StyledCol
                validationError={
                  addressFormData.addressData?.propertyDetails?.basement !== BasementType.NONE &&
                  validate(
                    'basementConversion',
                    addressFormData.addressData?.propertyDetails?.basementConversion
                  )
                }
              >
                <MultiSelect
                  disabled={
                    addressFormData.addressData?.propertyDetails?.basement === BasementType.NONE
                  }
                  value={addressFormData.addressData?.propertyDetails?.basementConversion || ''}
                  options={basementConversionTypes}
                  label={`${t('newAddressModal.objectDataTab.basementConversion')}*`}
                  onChange={(event) => onChangeBasementConversion(event.target.value)}
                  validation={validationMultiSelect}
                  formFieldName="propertyDetails.basementConversion"
                />
              </StyledCol>
            </StyledColContainer>
          )}
          {objectType === ObjectType.HOUSE && (
            <StyledColContainer>
              <StyledCol
                validationError={validate(
                  'outdoorFacilities',
                  addressFormData.addressData?.propertyDetails?.outdoorFacilities
                )}
              >
                <MultiSelect
                  value={addressFormData.addressData?.propertyDetails?.outdoorFacilities || ''}
                  options={outdoorFacilitiesTypes}
                  label={`${t('newAddressModal.objectDataTab.outdoorFacilities')}*`}
                  onChange={(event) => onChangeOutdoorFacilities(event.target.value)}
                  validation={validationMultiSelect}
                  formFieldName="propertyDetails.outdoorFacilities"
                />
              </StyledCol>
            </StyledColContainer>
          )}
          {objectType === ObjectType.APARTMENT && (
            <StyledColContainer>
              <StyledCol>
                <Input
                  value={addressFormData.addressData?.propertyDetails?.floorNumber}
                  onChange={(event) => onChangeFloors(event.target.value)}
                  onBlur={(event) => onChangeFloors(event.target.value, true)}
                  onKeyPress={(event) => handleNumberInput(event)}
                  defaultValue={addressFormData.addressData?.propertyDetails?.floorNumber}
                  label={`${t('newAddressModal.objectDataTab.floor')}`}
                  helperText="max. 100"
                />
              </StyledCol>
            </StyledColContainer>
          )}
          {objectType !== ObjectType.HOUSE && (
            <StyledColContainer>
              <StyledCol>
                <RadioGroupButtons
                  options={elevatorSelect}
                  label={t('newAddressModal.objectDataTab.elevator')}
                  onChange={(event) => onChangeElevator(event.target.value)}
                  value={addressFormData.addressData?.propertyDetails?.elevator}
                />
              </StyledCol>
            </StyledColContainer>
          )}
          {objectType === ObjectType.APARTMENT && (
            <StyledColContainer>
              <StyledCol>
                <RadioGroupButtons
                  options={gardenSelect}
                  label={t('newAddressModal.objectDataTab.garden')}
                  onChange={(event) => onChangeGarden(event.target.value)}
                  value={addressFormData.addressData?.propertyDetails?.garden}
                />
              </StyledCol>
            </StyledColContainer>
          )}
          <StyledColContainer>
            <StyledCol>
              <Input
                value={addressFormData.addressData?.propertyDetails?.garages}
                onChange={(event) => onChangeGarages(event.target.value)}
                onBlur={(event) => onChangeGarages(event.target.value, true)}
                onKeyPress={(event) => handleNumberInput(event)}
                defaultValue={addressFormData.addressData?.propertyDetails?.garages}
                label={`${t('newAddressModal.objectDataTab.garage')}`}
                helperText={objectType === ObjectType.HOUSE ? 'max. 4' : null}
              />
            </StyledCol>
          </StyledColContainer>
          {objectType !== ObjectType.HOUSE && (
            <StyledColContainer>
              <StyledCol>
                <Input
                  value={addressFormData.addressData?.propertyDetails?.outdoorParkingSpaces}
                  onChange={(event) => onChangeOutdoorParkingSpaces(event.target.value)}
                  onBlur={(event) => onChangeOutdoorParkingSpaces(event.target.value, true)}
                  onKeyPress={(event) => handleNumberInput(event)}
                  defaultValue={addressFormData.addressData?.propertyDetails?.outdoorParkingSpaces}
                  label={`${t('newAddressModal.objectDataTab.parkingPlace')}`}
                />
              </StyledCol>
            </StyledColContainer>
          )}
          <StyledColContainer>
            <StyledCol>
              <MultiSelect
                value={
                  addressFormData.addressData?.propertyDetails?.energyCertificate?.energyClass || ''
                }
                options={energyClassTypes}
                label={`${t('newAddressModal.objectDataTab.energyCertificate.energyClass')}`}
                onChange={(event) => onChangeEnergyClass(event.target.value)}
              />
            </StyledCol>
          </StyledColContainer>
        </StyledRowContainer>
      </StyledContainer>
      <StyledContinueButtonContainer>
        <Button kind="primary" onClick={() => onBackClick()}>
          {t('common.backButton')}
        </Button>
        <Button kind="primary" onClick={() => onContinueClick()}>
          {t('common.continueButton')}
        </Button>
      </StyledContinueButtonContainer>
    </>
  )
}

export default ValueBasicInformationForm
